import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdOutlineAccountBalance,
  MdCheckCircle,
  MdErrorOutline,
  MdSecurity,
} from "react-icons/md";
import {
  TbCurrencyRupee,
  TbLoader2,
  TbShieldCheck,
  TbAlertTriangle,
} from "react-icons/tb";
import { FiCheck, FiCopy, FiExternalLink } from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../../Utils/axiosInstance";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PenaltyPaymentModal = ({
  isOpen,
  onClose,
  selectedPenalties = [],
  user,
  onSuccess,
}) => {
  const [animate, setAnimate] = useState(false);
  const [step, setStep] = useState("review"); // "review" | "processing" | "success" | "failed"
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastPaymentData, setLastPaymentData] = useState(null);

  useLockBodyScroll(isOpen);

  const totalAmount = selectedPenalties.reduce(
    (sum, p) => sum + (p.amounts?.totalAmount || 0),
    0
  );

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      setStep("review");
      setLoading(false);
      setErrorMessage("");
      setLastPaymentData(null);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (step === "processing") return;
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  if (!isOpen) return null;

  const handlePayViaRazorpay = async () => {
    if (!selectedPenalties.length || totalAmount <= 0) {
      toast.error("Please select at least one valid penalty to pay");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      // 1. Ensure Razorpay script is loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setLoading(false);
        setErrorMessage(
          "Could not load Razorpay payment gateway. Please check your internet connection."
        );
        setStep("failed");
        return;
      }

      // 2. Initiate payment order via backend
      let orderData;
      try {
        const orderRes = await axios.post("/recharge/initiate-recharge", {
          amount: totalAmount,
        });

        if (!orderRes.data?.success) {
          setLoading(false);
          setErrorMessage(
            orderRes.data?.message || "Failed to create payment order."
          );
          setStep("failed");
          return;
        }
        orderData = orderRes.data.data;
      } catch (err) {
        setLoading(false);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to initiate payment. Please try again.";
        setErrorMessage(msg);
        setStep("failed");
        return;
      }

      const { orderId, razorpayKeyId, orderDetails } = orderData;
      setLoading(false);
      setStep("processing");

      // 3. Open Razorpay Checkout modal
      const options = {
        key: razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderDetails?.amount || Math.round(totalAmount * 100),
        currency: orderDetails?.currency || "INR",
        name: "PCM Exports",
        description: `Supplier Penalty Payment (${selectedPenalties.length} ${
          selectedPenalties.length === 1 ? "penalty" : "penalties"
        })`,
        order_id: orderId,
        prefill: {
          name:
            user?.fullName ||
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
            "Supplier",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: { color: "#772037" },
        modal: {
          ondismiss: () => {
            setStep("review");
            toast("Payment cancelled", { icon: "ℹ️" });
          },
        },
        handler: async (response) => {
          setStep("processing");
          try {
            // 4. Verify payment with recharge/verify-payments
            const verifyRes = await axios.post("/recharge/verify-payments", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyRes.data?.success) {
              // 5. Mark selected penalties as Paid on backend
              const penaltyIds = selectedPenalties.map((p) => p._id);
              try {
                const payRes = await axios.post("/penalties/pay-via-razorpay", {
                  penaltyIds,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                });

                setLastPaymentData({
                  paymentId: response.razorpay_payment_id,
                  orderId: response.razorpay_order_id,
                  amount: totalAmount,
                  count: penaltyIds.length,
                  paidPenaltyIds: penaltyIds,
                  date: new Date().toISOString(),
                });
                setStep("success");
                toast.success(
                  payRes.data?.message || "Penalties paid successfully!"
                );
                onSuccess && onSuccess();
              } catch (penaltyErr) {
                console.error("Failed to mark penalties paid:", penaltyErr);
                setErrorMessage(
                  penaltyErr.response?.data?.message ||
                    "Payment was verified, but failed to update penalty status. Please contact support."
                );
                setStep("failed");
              }
            } else {
              setErrorMessage(
                verifyRes.data?.message || "Payment verification failed."
              );
              setStep("failed");
            }
          } catch (err) {
            console.error("Verification error:", err);
            setErrorMessage(
              err.response?.data?.message ||
                "Payment verification error. If money was deducted, it will be reconciled."
            );
            setStep("failed");
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setLoading(false);
      setErrorMessage(err.message || "An unexpected error occurred.");
      setStep("failed");
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== "processing") handleClose();
      }}
    >
      <div
        className={`bg-white dark:bg-gray-900 w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${
          animate
            ? "scale-100 translate-y-0"
            : "scale-95 md:translate-y-4 translate-y-full"
        }`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center justify-between flex-shrink-0">
          <button
            type="button"
            disabled={step === "processing"}
            onClick={handleClose}
            className="text-2xl leading-none text-white/90 hover:text-white transition disabled:opacity-30 cursor-pointer"
          >
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-lg text-white">
            {step === "success"
              ? "Payment Successful"
              : step === "failed"
              ? "Payment Failed"
              : "Pay Supplier Penalties"}
          </div>
          {step !== "processing" ? (
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition cursor-pointer"
            >
              <MdClose size={18} />
            </button>
          ) : (
            <div className="w-8" />
          )}
        </div>

        {/* Modal Content based on step */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1">
          {/* STEP 1: REVIEW */}
          {step === "review" && (
            <div className="space-y-4">
              {/* Summary card */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-[#772037]/5 via-[#772037]/[0.02] to-transparent border border-[#772037]/15">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Total Payable Amount
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#772037]/10 text-[#772037] font-bold">
                    {selectedPenalties.length}{" "}
                    {selectedPenalties.length === 1 ? "Penalty" : "Penalties"}
                  </span>
                </div>
                <div className="text-3xl font-extrabold text-[#772037] flex items-baseline gap-1">
                  ₹{totalAmount.toLocaleString("en-IN")}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  All selected penalties will be marked as Paid immediately upon
                  successful transaction.
                </p>
              </div>

              {/* Penalty items preview */}
              <div className="space-y-2">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Items to Settle ({selectedPenalties.length})
                </span>
                <div className="max-h-48 overflow-y-auto space-y-2 pr-1 no-scrollbar">
                  {selectedPenalties.map((penalty) => (
                    <div
                      key={penalty._id}
                      className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-xs"
                    >
                      <div className="min-w-0 pr-2">
                        <span className="font-bold text-gray-800 dark:text-gray-200 block truncate font-mono">
                          {penalty.uniquePenaltyId}
                        </span>
                        <span className="text-[11px] text-gray-500 truncate block">
                          {penalty.penaltyReason?.replace(/_/g, " ") ||
                            "Return Penalty"}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="font-extrabold text-[#772037] text-sm block">
                          ₹
                          {(
                            penalty.amounts?.totalAmount || 0
                          ).toLocaleString("en-IN")}
                        </span>
                        <span className="text-[10px] text-amber-600 font-semibold uppercase">
                          {penalty.paymentStatus}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Gateway badge */}
              <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200/80 text-xs">
                <TbShieldCheck size={18} className="text-emerald-600 flex-shrink-0" />
                <span>
                  Secured by <strong>Razorpay</strong>. UPI, Credit/Debit Cards,
                  Netbanking & Wallets supported.
                </span>
              </div>

              {/* Actions */}
              <div className="pt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-xs hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePayViaRazorpay}
                  disabled={loading}
                  className="flex-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#772037] to-[#924d5f] hover:from-[#651a2e] hover:to-[#772037] text-white font-bold text-sm shadow-md shadow-[#772037]/20 flex items-center justify-center gap-2 transition disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <TbLoader2 className="animate-spin text-lg" />
                      <span>Initiating...</span>
                    </>
                  ) : (
                    <>
                      <span>Pay ₹{totalAmount.toLocaleString("en-IN")}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: PROCESSING */}
          {step === "processing" && (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-[#772037]/10 flex items-center justify-center text-[#772037] mx-auto animate-pulse">
                <TbLoader2 size={36} className="animate-spin" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Processing Your Payment
                </h3>
                <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                  Please complete the payment in the Razorpay window. Do not
                  refresh or close this page.
                </p>
              </div>
            </div>
          )}

          {/* STEP 3: SUCCESS */}
          {step === "success" && lastPaymentData && (
            <div className="text-center space-y-4 py-2">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-inner">
                <MdCheckCircle size={40} />
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Payment Cleared Successfully!
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Your payment has been verified and {lastPaymentData.count}{" "}
                  {lastPaymentData.count === 1 ? "penalty has" : "penalties have"}{" "}
                  been settled.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-xs space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount Paid:</span>
                  <span className="font-extrabold text-gray-900 dark:text-white">
                    ₹{lastPaymentData.amount.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Transaction ID:</span>
                  <span className="font-mono text-gray-800 dark:text-gray-200 font-semibold text-[11px]">
                    {lastPaymentData.paymentId}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Settlement Date:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {new Date(lastPaymentData.date).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="w-full px-4 py-2.5 rounded-xl bg-primaryColor text-white font-bold text-sm shadow-md hover:bg-primaryColor/90 transition cursor-pointer"
              >
                Done
              </button>
            </div>
          )}

          {/* STEP 4: FAILED */}
          {step === "failed" && (
            <div className="text-center space-y-4 py-2">
              <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto shadow-inner">
                <MdErrorOutline size={40} />
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Payment Could Not Be Completed
                </h3>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 max-w-xs mx-auto">
                  {errorMessage ||
                    "We encountered an issue processing your payment. Please try again or contact support."}
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-xs hover:bg-gray-50 dark:hover:bg-gray-800 transition cursor-pointer"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => setStep("review")}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-primaryColor text-white font-bold text-xs shadow hover:bg-primaryColor/90 transition cursor-pointer"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PenaltyPaymentModal;
